import type { SettingType } from '@/types/app';

/**
 * Flow-level "what to do next" guidance. This is the cross-page counterpart of the
 * settings confirmation wizard: where `confirmationWizard` decides the next action
 * *within* the offer page's settings card, this decides the single next action
 * *anywhere in the quote flow* (offer page → add-ons → overview → application).
 *
 * Both the canonical guidance line (rendered by the progress bar) and the inline
 * highlight on the actual control derive from the same `getFlowGuidance` result, so
 * the narrated next step and the highlighted button can never drift apart.
 */

/**
 * The control the inline highlight attaches to — the one element the visitor (human
 * or browsing agent) should act on next. `null` on the canonical line means the step
 * already carries its own in-context cue (the settings card), so there is no extra
 * control to mark.
 */
export type FlowTarget = 'choose-offer' | 'proceed-addons' | 'confirm-review' | 'application-next';

export interface FlowGuidance {
	/** i18n key (under `header.guidance.*`) for the next-step message on the canonical line. */
	messageKey: string;
	/** Which control the inline highlight binds to, or null when the canonical line stands alone. */
	target: FlowTarget | null;
}

/**
 * Route + derived state the guidance is computed from. Kept as plain data (no router,
 * no hooks) so the decision logic is unit-testable in isolation.
 */
export interface FlowGuidanceInput {
	pathname: string;
	/** Offer page only: false while quotes are still generating — nothing to act on yet. */
	quotesReady: boolean;
	/**
	 * Overview page only: when false the confirm button is hidden (only the advisor entry
	 * remains), so guidance must not point at a control that isn't rendered.
	 */
	directApplicationAllowed: boolean;
	/** Offer page only: first settings step still pending, or null when all are confirmed. */
	firstPendingSettingsStep: SettingType | null;
	/**
	 * Add-ons page only: whether the client actually has add-on products to choose. When
	 * false the step is a pass-through, so guidance skips ahead to the review copy.
	 */
	addOnsAvailable: boolean;
	/** Application route only: the current sub-step number from the URL, else null. */
	applicationStep: number | null;
}

// Route prefixes mirror the flow routes in App.tsx and ProgressIndicator's ROUTE_CONFIG.
const ROUTE_AANBOD = '/aanbod';
const ROUTE_AANVULLING = '/aanvulling';
const ROUTE_OVERZICHT = '/overzicht';
const ROUTE_AANVRAAG = '/aanvraag';

// The quote-flow routes that carry the guidance bar. Kept together so `isFlowRoute` and
// `getFlowGuidance` reason about the same set.
const FLOW_ROUTE_PREFIXES = [ROUTE_AANBOD, ROUTE_AANVULLING, ROUTE_OVERZICHT, ROUTE_AANVRAAG];

/**
 * True when the pathname is one of the quote-flow routes that carries the guidance bar.
 * Lets the bar reserve its vertical space while the message is momentarily absent (e.g.
 * quotes still generating or recalculating on the offer page), so hiding the line leaves
 * the page in place instead of jumping up and back.
 */
export function isFlowRoute(pathname: string): boolean {
	return FLOW_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Last application sub-step ("Afronden"); mirrors ProgressIndicator's finalStep.appStep. */
export const FINAL_APPLICATION_STEP = 5;

/**
 * Per-step next-action copy for the offer page. When a settings card still shows an orange
 * warning, the top bar names the specific panel to act on next (the one `Kiezen` would open
 * — see `getNextGuidanceStep`) rather than a generic "complete your details".
 */
const SETTINGS_STEP_MESSAGE_KEYS: Record<SettingType, string> = {
	employees: 'header.guidance.settingsEmployees',
	absence: 'header.guidance.settingsAbsence',
	coverage: 'header.guidance.settingsCoverage'
};

/**
 * The single next action for the current route, or null when there is nothing to guide
 * (not on a flow route, or quotes are still generating on the offer page).
 */
export function getFlowGuidance(input: FlowGuidanceInput): FlowGuidance | null {
	const {
		pathname,
		quotesReady,
		directApplicationAllowed,
		firstPendingSettingsStep,
		addOnsAvailable,
		applicationStep
	} = input;

	// Step 2 — offer page: confirm the settings first, then choose an offer.
	if (pathname.startsWith(ROUTE_AANBOD)) {
		if (!quotesReady) return null;
		if (firstPendingSettingsStep !== null) {
			// Point directly at the panel the visitor must resolve next (the one that opens on
			// Kiezen). The settings card carries its own orange warning, so there is no
			// separate control for the inline highlight to mark here.
			return { messageKey: SETTINGS_STEP_MESSAGE_KEYS[firstPendingSettingsStep], target: null };
		}
		return { messageKey: 'header.guidance.chooseOffer', target: 'choose-offer' };
	}

	// Step 3 — choose add-ons, then review. When the client has no add-on products the step
	// is a pass-through: skip straight to the review copy, still pointing at the proceed
	// button (which now advances directly to the overview) so guidance names the real next
	// action instead of asking the visitor to choose add-ons that don't exist.
	if (pathname.startsWith(ROUTE_AANVULLING)) {
		const messageKey = addOnsAvailable ? 'header.guidance.addOns' : 'header.guidance.review';
		return { messageKey, target: 'proceed-addons' };
	}
	if (pathname.startsWith(ROUTE_OVERZICHT)) {
		// With direct application disabled the confirm button is hidden and the only forward
		// action is discussing with an advisor, so narrate that instead of pointing the
		// inline highlight at a control that isn't rendered.
		if (!directApplicationAllowed) {
			return { messageKey: 'header.guidance.reviewAdvisor', target: null };
		}
		return { messageKey: 'header.guidance.review', target: 'confirm-review' };
	}

	// Steps 4–5 — application form. Field-level validation stays in the form itself;
	// here we only point at the advance button and switch copy on the final step.
	if (pathname.startsWith(ROUTE_AANVRAAG)) {
		const messageKey =
			applicationStep === FINAL_APPLICATION_STEP
				? 'header.guidance.finish'
				: 'header.guidance.application';
		return { messageKey, target: 'application-next' };
	}

	return null;
}
